import { useContext, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import ReactInputMask from 'react-input-mask';
import { Button } from '@shared/ui/button';
import { InputField } from '@shared/ui/input-field';
import { LoadingWrapper } from '@shared/ui/loading-wrapper';
import { FormCard } from '@shared/ui/form-card';
import { useAppDispatch, useAppSelector } from '../../../app/store/store';
import { AbilityContext } from '@shared/lib/ability';
import {
  selectCurrentEmployee,
  fetchEmployeeById,
  partialUpdateEmployee,
} from '../../../entities/employee/model';
import { yupResolver } from '@hookform/resolvers/yup';
import { employeeSchema } from '../lib/employee-schema';

interface IDefaultValues {
  id: string;
  first_name: string;
  last_name: string;
  patronymic: string;
  mobile_phone: string;
  whatsapp: string;
  telegram: string;
  email: string;
}

const CompaniesEmployeeEdit = () => {
  const { employeeId } = useParams();
  const employee = useAppSelector(selectCurrentEmployee);
  const dispatch = useAppDispatch();
  const status = useAppSelector((state) => state.employees.status);
  const ability = useContext(AbilityContext);
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors, isDirty },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } = useForm<IDefaultValues | any>({
    mode: 'onBlur',
    resolver: yupResolver(employeeSchema),
    shouldUnregister: false,
    defaultValues: {
      first_name: employee?.first_name,
      last_name: employee?.last_name,
      patronymic: employee?.patronymic,
      mobile_phone: employee?.mobile_phone,
      whatsapp: employee?.whatsapp,
      telegram: employee?.telegram,
      email: employee?.email,
    },
  });

  useEffect(() => {
    if (status === 'idle' && employeeId) {
      dispatch(fetchEmployeeById(employeeId));
    }
  }, [status, dispatch, employeeId]);

  useEffect(() => {
    reset({
      first_name: employee?.first_name,
      last_name: employee?.last_name,
      patronymic: employee?.patronymic,
      mobile_phone: employee?.mobile_phone,
      whatsapp: employee?.whatsapp,
      telegram: employee?.telegram,
      email: employee?.email,
    });
  }, [reset, employee]);

  const onSubmit = async (data: IDefaultValues) => {
    if (ability.can('editemployee', 'username') && employeeId) {
      dispatch(partialUpdateEmployee({ id: employeeId, data }));
    }
    navigate(-1);
  };

  return (
    <LoadingWrapper type="main" status={status}>
      <FormCard
        title="Данные сотрудника"
        description="Изменения отобразятся в карточке компании и в списках"
      >
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-1">
          {/* ФИО — три поля в строку */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-4">
            <InputField
              id="last_name"
              inputName="last_name"
              label="Фамилия"
              errors={errors}
              register={register}
            />
            <InputField
              id="first_name"
              inputName="first_name"
              label="Имя"
              errors={errors}
              register={register}
            />
            <InputField
              id="patronymic"
              inputName="patronymic"
              label="Отчество"
              errors={errors}
              register={register}
            />
          </div>

          {/* Email + телефон */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
            <InputField
              id="email"
              inputName="email"
              label="Email"
              errors={errors}
              register={register}
            />
            <Controller
              name="mobile_phone"
              control={control}
              render={({ field }) => (
                <InputField
                  {...field}
                  as={ReactInputMask}
                  mask="+7 (***) ***-**-**"
                  id="mobile_phone"
                  inputName="mobile_phone"
                  label="Телефон"
                  errors={errors}
                  register={register}
                />
              )}
            />
          </div>

          {/* Мессенджеры */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
            <InputField
              id="telegram"
              inputName="telegram"
              label="Telegram"
              errors={errors}
              register={register}
            />
            <InputField
              id="whatsapp"
              inputName="whatsapp"
              label="WhatsApp"
              errors={errors}
              register={register}
            />
          </div>

          <div className="pt-4 flex gap-2">
            <Button type="submit" disabled={!isDirty}>
              Сохранить
            </Button>
            <Button isWhite onClick={() => navigate(-1)}>
              Отмена
            </Button>
          </div>
        </form>
      </FormCard>
    </LoadingWrapper>
  );
};

export default CompaniesEmployeeEdit;
